import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { AlertTriangle, ShieldAlert, ExternalLink } from "lucide-react";

const TABS = [
  { v: "integrity", l: "Integrity review required" },
  { v: "missing_required", l: "Missing required evidence" },
  { v: "orphaned", l: "Orphaned / unavailable evidence" },
];

export default function AdminEvidenceIssues() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "integrity";
  const [data, setData] = useState({ total: 0, items: [] });

  useEffect(() => {
    api.get("/admin/evidence-issues", { params: { type } }).then(({ data }) => setData(data)).catch(() => setData({ total: 0, items: [] }));
  }, [type]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900"><ShieldAlert className="h-6 w-6 text-amber-500" /> Evidence issues</h2>
        <p className="text-slate-500">Every record here is an authoritative evidence exception requiring review.</p>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.v} data-testid={`evidence-tab-${t.v}`} onClick={() => setParams({ type: t.v })}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${type === t.v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 text-sm text-slate-500">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <span data-testid="evidence-issues-total"><span className="font-semibold text-slate-900">{data.total}</span> record(s)</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            {type === "integrity" ? (
              <tr><th className="px-5 py-3">File</th><th className="px-5 py-3">Job</th><th className="px-5 py-3">Integrity</th><th className="px-5 py-3">Checked</th><th className="px-5 py-3">Actions</th></tr>
            ) : type === "missing_required" ? (
              <tr><th className="px-5 py-3">Job</th><th className="px-5 py-3">Service type</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Detail</th></tr>
            ) : (
              <tr><th className="px-5 py-3">Job</th><th className="px-5 py-3">Attempt</th><th className="px-5 py-3">Server</th><th className="px-5 py-3">Detail</th></tr>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {type === "integrity" && data.items.map((it) => (
              <tr key={it.file_id} data-testid={`integrity-row-${it.file_id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-slate-700">{it.filename || it.file_id}</td>
                <td className="px-5 py-3">{it.order_id ? <button onClick={() => nav(`/admin/jobs/${it.order_id}`)} className="font-semibold text-indigo-600 hover:underline">View job</button> : "—"}</td>
                <td className="px-5 py-3"><span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">{it.integrity_status || "mismatch"}</span></td>
                <td className="px-5 py-3 text-slate-400">{it.integrity_checked_at ? new Date(it.integrity_checked_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3"><a href={`${API}/files/${it.file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open</a></td>
              </tr>
            ))}
            {type === "orphaned" && data.items.map((it, i) => (
              <tr key={i} data-testid={`orphaned-row-${i}`} className="hover:bg-slate-50">
                <td className="px-5 py-3">{it.order_id ? <button onClick={() => nav(`/admin/jobs/${it.order_id}`)} className="font-semibold text-indigo-600 hover:underline">#{it.job_number || "job"}</button> : "—"}</td>
                <td className="px-5 py-3 text-slate-700">#{it.attempt_number}</td>
                <td className="px-5 py-3 text-slate-500">{it.server_name}</td>
                <td className="px-5 py-3 text-amber-700">{it.detail}</td>
              </tr>
            ))}
            {type === "missing_required" && data.items.map((it, i) => (
              <tr key={i} data-testid={`missing-row-${i}`} className="hover:bg-slate-50">
                <td className="px-5 py-3">{it.order_id ? <button onClick={() => nav(`/admin/jobs/${it.order_id}`)} className="font-semibold text-indigo-600 hover:underline">#{it.job_number || "job"}</button> : "—"}</td>
                <td className="px-5 py-3 text-slate-700">{it.service_type}</td>
                <td className="px-5 py-3 text-slate-500">{it.status}</td>
                <td className="px-5 py-3 text-red-700">{it.detail}</td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-400">No evidence issues 🎉</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
